import { TFunction } from "i18next";
import { PartyTotals } from "../../shared/Analytics";
import { costEntries, CostType } from "../../shared/CaseConfiguration";
import { ZERO } from "../../shared/bigUtil.ts";

/**
 * Sentinel for "recovery is not applicable to this row" (e.g. CFA success
 * fee, no costRecoveries rule configured for this type). Distinct from `0`
 * (recovery was possible per config but yielded nothing this outcome) and
 * `undefined` (the row isn't a cost row at all — quantum, settlement, …).
 */
export const RECOVERY_NOT_APPLICABLE: unique symbol = Symbol(
  "RECOVERY_NOT_APPLICABLE",
);
export type Recovered = number | typeof RECOVERY_NOT_APPLICABLE;

/**
 * A row in the financial breakdown table. Two-column money layout: `amount`
 * is the cost the party absorbed (left/right column today), `recovered` is
 * what the party gets back from the loser for this row (middle column).
 */
export type BreakdownRow = {
  label: string;
  amount: number;
  recovered?: Recovered;
  /** Display-only subtotal row; not a real contribution to net result. */
  subtotal?: boolean;
  /**
   * Display-only sub-row (e.g. settlement-balance waterfall slice). Rendered
   * indented; doesn't contribute to net result.
   */
  detail?: boolean;
  /**
   * Icon prefix in the label cell. The dialog also uses this to align indented
   * detail sub-rows with the label text (past the icon's width).
   */
  icon?: "settlement";
  /** Counterparty names for the info-icon tooltip on recovery rows. */
  counterparties?: ReadonlyArray<string>;
};

/**
 * Recovered amount to show on a per-type cost row, read straight off the
 * server-attributed totals (no re-derivation). Mirrors how the row's AMOUNT
 * folds in the enforcement slice: budget recovery (`costsRecovered[type]`) plus
 * this type's enforcement-stage recovery (`enforcementCost[type].recovered`).
 * Returns `RECOVERY_NOT_APPLICABLE` only when the type carries no recovery
 * signal at all (no rule); a present `0` means a rule applied but nothing was
 * recovered.
 */
function recoveredForCostRow(type: CostType, party: PartyTotals): Recovered {
  const budget = party.costsRecovered[type];
  const enforcement = party.enforcementCost[type]?.recovered;
  if (budget === undefined && enforcement === undefined)
    return RECOVERY_NOT_APPLICABLE;
  return (budget ?? 0) + (enforcement ?? 0);
}

/**
 * Recovered amount attributable to Enforcement-stage costs, summed across all
 * cost types — the recovered cell on the "of which: enforcement costs" detail
 * row. Informational: this £ is already part of the parent subtotal's total.
 */
function recoveredAtEnforcement(party: PartyTotals): number {
  let total = ZERO;
  for (const bucket of Object.values(party.enforcementCost)) {
    if (bucket?.recovered !== undefined) total = total.add(bucket.recovered);
  }
  return total.toNumber();
}

/**
 * Build the financial breakdown rows for the dialog directly from the
 * server-aggregated party totals. Layout reads like a bill, costs first:
 *
 *   1. Cost block — per-type cost rows, enforcement cost rows, agreement fees
 *      and the VAT on all of them (each carrying a `recovered` cell)
 *   2. Cost subtotal with recovered-total annotation (when >1 cost row)
 *   3. Adverse recoveries the party owes (separate row)
 *   4. Net legal cost subtotal
 *   5. Quantums + enforcement shortfall + settlement balance
 *
 * Subtotals are display-only — they don't contribute to `Party net result`.
 */
export function buildBreakdown(
  party: PartyTotals,
  t: TFunction,
): BreakdownRow[] {
  const rows: BreakdownRow[] = [];

  // === 1. Cost block (costs + enforcement costs + agreement fees) ===
  let costsSubtotal = ZERO;
  let recoveredSubtotal = ZERO;
  let costRowCount = 0;

  // Per-type cost row absorbs its enforcement-stage slice — the £ the party is
  // paying for enforcement-stage work of that type is realistic plan-apportioned
  // spend (e.g. £700 not £1,000 budget when one defendant isn't enforced
  // against), so the typed cost row total reads the full lawyer/barrister/court
  // bill including enforcement. The recovery side already sums all stages.
  const enforcementByType = (type: CostType): number => {
    const bucket = party.enforcementCost[type];
    if (!bucket) return 0;
    let sum = bucket.generic;
    for (const v of Object.values(bucket.perPayer)) sum += v;
    return sum;
  };

  for (const [type, amount] of costEntries(party.costs)) {
    const ecAmount = enforcementByType(type);
    const totalAmount = amount + ecAmount;
    if (totalAmount) {
      const recovered = recoveredForCostRow(type, party);
      rows.push({
        label: t(`outcomeDialog.costTypes.${type}`),
        amount: totalAmount,
        recovered,
      });
      costsSubtotal = costsSubtotal.add(totalAmount);
      if (typeof recovered === "number")
        recoveredSubtotal = recoveredSubtotal.add(recovered);
      costRowCount += 1;

      // Indent the settlement costs of this type as detail sub-rows under
      // its row. Their amounts ARE already in the parent row's total; this
      // just surfaces what's in there so it doesn't look like a phantom.
      for (const slc of party.settlementLegalCosts) {
        if (slc.type !== type) continue;
        rows.push({
          label: t("outcomeDialog.bill.settlementLegalCost"),
          amount: slc.amount,
          detail: true,
        });
      }
    }
  }

  // A cost type may have ONLY an enforcement-stage slice (no other-stage budget)
  // — `costEntries(party.costs)` won't iterate it. Catch those here.
  for (const type of Object.keys(party.enforcementCost) as CostType[]) {
    if (party.costs[type] !== undefined) continue; // already handled above
    const ecAmount = enforcementByType(type);
    if (!ecAmount) continue;
    const recovered = recoveredForCostRow(type, party);
    rows.push({
      label: t(`outcomeDialog.costTypes.${type}`),
      amount: ecAmount,
      recovered,
    });
    costsSubtotal = costsSubtotal.add(ecAmount);
    if (typeof recovered === "number")
      recoveredSubtotal = recoveredSubtotal.add(recovered);
    costRowCount += 1;
  }

  // Aggregated total across all per-type enforcement slices — surfaced as the
  // "of which: enforcement costs" detail row under the Subtotal. Big-arithmetic
  // mirrors `costsSubtotal` / `recoveredSubtotal` so float drift doesn't slip
  // into the subtotal's detail row.
  let enforcementCostTotal = ZERO;
  for (const bucket of Object.values(party.enforcementCost)) {
    if (!bucket) continue;
    enforcementCostTotal = enforcementCostTotal.add(bucket.generic);
    for (const v of Object.values(bucket.perPayer))
      enforcementCostTotal = enforcementCostTotal.add(v);
  }

  for (const fee of party.agreementFees) {
    if (fee.amount) {
      // The CFA base fee carries its own recovery (the covered "deemed costs",
      // attributed server-side); the success fee, no-win fee, and regular fees
      // are never recoverable, so they have no `recovered` sub-element.
      const recovered: Recovered =
        fee.recovered !== undefined ? fee.recovered : RECOVERY_NOT_APPLICABLE;
      rows.push({
        label: t(`outcomeDialog.feeTypes.${fee.recipient}.${fee.type}`),
        amount: fee.amount,
        recovered,
      });
      costsSubtotal = costsSubtotal.add(fee.amount);
      if (typeof recovered === "number")
        recoveredSubtotal = recoveredSubtotal.add(recovered);
      costRowCount += 1;
    }
  }

  // The VAT a party that cannot reclaim it pays on all of the above, with
  // what it recovers of it: a row of its own, as on a bill of costs. The
  // rows above are without it.
  if (party.vat?.paid) {
    const recovered: Recovered = party.vat.recovered ?? RECOVERY_NOT_APPLICABLE;
    rows.push({
      label: t("outcomeDialog.bill.vat"),
      amount: party.vat.paid,
      recovered,
    });
    costsSubtotal = costsSubtotal.add(party.vat.paid);
    if (typeof recovered === "number")
      recoveredSubtotal = recoveredSubtotal.add(recovered);
    costRowCount += 1;
  }

  // === 2. Cost subtotal — only when there is more than one cost-block row ===
  if (costRowCount > 1) {
    rows.push({
      label: t("outcomeDialog.bill.costsSubtotal"),
      amount: costsSubtotal.toNumber(),
      recovered: recoveredSubtotal.toNumber(),
      subtotal: true,
    });
    if (!enforcementCostTotal.eq(0)) {
      rows.push({
        label: t("outcomeDialog.bill.subtotalOfWhichEnforcement"),
        amount: enforcementCostTotal.toNumber(),
        recovered: recoveredAtEnforcement(party),
        detail: true,
      });
    }
  }

  // === 3. Adverse recoveries the party owes (counterparty's award) ===
  if (party.recoveriesToPay.total) {
    rows.push({
      label: t("outcomeDialog.recoveries.toPay"),
      amount: party.recoveriesToPay.total,
      counterparties: party.recoveriesToPay.toRecipients,
    });
  }

  // === 4. Net legal cost subtotal — when costs exist and either recoveries
  //         or adverse recoveries are present ===
  if (
    costRowCount > 0 &&
    (recoveredSubtotal.gt(0) || party.recoveriesToPay.total)
  ) {
    rows.push({
      label: t("outcomeDialog.bill.netCostSubtotal"),
      amount: costsSubtotal
        .add(recoveredSubtotal)
        .add(party.recoveriesToPay.total)
        .toNumber(),
      subtotal: true,
    });
  }

  // === 5. Quantums + enforcement context + settlement ===
  for (const [name, amount] of Object.entries(party.quantumsAwarded)) {
    if (amount)
      rows.push({
        label: t("outcomeDialog.bill.quantumAwarded", { name }),
        amount,
      });
  }
  for (const [name, amount] of Object.entries(party.quantumsToPay)) {
    if (amount)
      rows.push({
        label: t("outcomeDialog.bill.quantumToPay", { name }),
        amount,
      });
  }
  if (party.enforcementShortfall)
    rows.push({
      label: t("outcomeDialog.bill.enforcementShortfall"),
      amount: party.enforcementShortfall,
    });
  // === Settlements — one parent row per settlement with breakdown sub-rows. ===
  // Falls back to the aggregate `settlementBalance` line when no per-settlement
  // breakdown is available (legacy cached results).
  if (party.settlementBreakdowns.length > 0) {
    for (const b of party.settlementBreakdowns) {
      rows.push({
        label: t("outcomeDialog.bill.settlementWith", {
          name: b.counterpartyName,
        }),
        amount: b.amount,
        icon: "settlement",
      });
      if (b.ofSettlement)
        rows.push({
          label: t("outcomeDialog.bill.settlementCoversSettlementCosts"),
          amount: b.ofSettlement,
          detail: true,
        });
      if (b.towardsPayer)
        rows.push({
          label: t("outcomeDialog.bill.settlementCoversAdverseCosts", {
            name: b.counterpartyName,
          }),
          amount: b.towardsPayer,
          detail: true,
        });
      if (b.generic)
        rows.push({
          label: t("outcomeDialog.bill.settlementCoversOtherCosts"),
          amount: b.generic,
          detail: true,
        });
      for (const [quantumName, amount] of Object.entries(b.quantums)) {
        if (amount)
          rows.push({
            label: t("outcomeDialog.bill.settlementCoversQuantum", {
              name: quantumName,
            }),
            amount,
            detail: true,
          });
      }
    }
  }

  return rows;
}
